import { pathOr } from 'ramda';

import {
  getProductPackFormulaIds,
  getProductPackItems,
  ProductPack
} from '@/models/core/ProductPack';
import { getPackItemFormulaId, getPackItemUsage, PackItem } from '@/models/core/PackItem';
import { Size } from '@/models/core/values';
import { Ingredient } from '@/models/core/Ingredient';
import { InciList } from '@/models/core/InciList';

const PRODUCT_REFERENCE = 'eyeContourSerum';
const PRODUCT_SIZE = Size(15, 'ml');
const PRODUCT_CATEGORIES = ['treat'];

export const getGenericFormulaIds = eyeSerumGenericFormulasDto =>
  eyeSerumGenericFormulasDto.map(formulaDto => Object.keys(formulaDto)[0]);

export const getFormulaDataById = (formulaId, eyeSerumGenericFormulasDto) => {
  const dto = eyeSerumGenericFormulasDto.find(formulaDto => {
    const id = Object.keys(formulaDto)[0];

    return id === formulaId;
  });

  if (dto) {
    return dto[formulaId];
  }

  return null;
};

const getBottleComposition = (formulaId, eyeSerumGenericFormulasDto) => {
  const formula = getFormulaDataById(formulaId, eyeSerumGenericFormulasDto);

  return pathOr(null, ['formula', 'formulasOptions', 'oneFormula', 'compositionBottle1'], formula);
};

const getEyeGenericFormulaUsage = (formulaId, eyeSerumGenericFormulasDto) => {
  const formula = getFormulaDataById(formulaId, eyeSerumGenericFormulasDto);

  return pathOr(null, ['formula', 'formulasOptions', 'oneFormula', 'usage'], formula);
};

const getPackItemFromFormula = (formulaId, eyeSerumGenericFormulasDto) => {
  const composition = getBottleComposition(formulaId, eyeSerumGenericFormulasDto);

  const ingredients = [composition.actif1, composition.actif2, composition.actif3]
    .filter(bottle => !!bottle)
    .map(bottle => Ingredient(bottle.name, bottle.nbOfCapsules));
  const usage = getEyeGenericFormulaUsage(formulaId, eyeSerumGenericFormulasDto);

  return PackItem({
    reference: PRODUCT_REFERENCE,
    size: PRODUCT_SIZE,
    usage,
    categories: PRODUCT_CATEGORIES,
    formulaId,
    ingredients
  });
};

/**
 * Check is productPack with eye serum generic formula in stock.
 *
 * @param {ProductPack} productPack
 * @param {Object} eyeSerumGenericFormulasDto
 * @return {boolean}
 */
export const isProductPackWithEyeSerumGenericFormulaInStock = (
  productPack,
  eyeSerumGenericFormulasDto
) => {
  const formulaIds = getProductPackFormulaIds(productPack);

  return formulaIds.some(formulaId => {
    const formulaData = getFormulaDataById(formulaId, eyeSerumGenericFormulasDto);

    return pathOr(false, ['inStock'], formulaData);
  });
};

/**
 * Get a list of product packs from eye serum generic formulas dto (one pack per generic formula).
 *
 * @param {Object} eyeSerumGenericFormulasDto
 * @return {ProductPack[]}
 */
export const getProductPacksFromGenericEyeFormulasDto = eyeSerumGenericFormulasDto => {
  const formulaIds = getGenericFormulaIds(eyeSerumGenericFormulasDto);

  return formulaIds
    .map(formulaId => getPackItemFromFormula(formulaId, eyeSerumGenericFormulasDto))
    .map(formula => ProductPack([formula]));
};

const isValidInciValue = value => typeof value === 'string' && value.trim() !== '';

/**
 * Get inci lists for product pack from generic formulas dto.
 *
 * @param {ProductPack} productPack
 * @param {Object} eyeSerumGenericFormulasDto
 * @return {InciList[]}
 */
export const getGenericSerumInciLists = (productPack, eyeSerumGenericFormulasDto) => {
  const items = getProductPackItems(productPack);

  return items.map(item => {
    const formulaId = getPackItemFormulaId(item);
    const usage = getPackItemUsage(item);
    const { activeInci, inci, excipientsFromActives } = getFormulaDataById(
      formulaId,
      eyeSerumGenericFormulasDto
    );

    const active = activeInci.split(', ').filter(isValidInciValue);
    const inactive = inci.split(', ').filter(isValidInciValue);
    const excipients = excipientsFromActives
      ? excipientsFromActives.split(', ').filter(isValidInciValue)
      : undefined;

    return InciList(usage, active, inactive, excipients);
  });
};
